import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Check, Copy, Download, Link2, Megaphone, QrCode, X,
} from 'lucide-react';

import {
  createStorefrontCampaign, createStorefrontLink, updateStorefrontCampaign, updateStorefrontLink,
} from './api';
import { XomicsSelect } from './parts';
import { errorMessage } from './ui';
import { qrMatrix, qrPath, qrSvg } from './qr';
import { channelLabel, st } from './StorefrontParts';

// Creating and changing campaigns and their links, and a link's QR code. The
// server checks every field and the manage permission; these forms only say
// what it answered, by its code.

const CODE_PATTERN = /^[a-z0-9-]{3,32}$/;
const STATUSES = ['active', 'paused', 'archived'];

// A date field's value for an epoch, on the viewer's calendar, and back.
function dayValue(at) {
  if (at === null || at === undefined) return '';
  const date = new Date(at);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function dayStart(value) {
  if (!value) return null;
  const [year, month, dayOfMonth] = value.split('-').map(Number);
  return new Date(year, month - 1, dayOfMonth).getTime();
}

function dayEnd(value) {
  const start = dayStart(value);
  return start === null ? null : start + 86400000 - 1;
}

// What a link's code turns into, for utm_campaign when nobody typed one.
export function slug(text) {
  return String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

function Dialog({ icon: Icon, title, detail, onClose, wide = false, children }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className={`xo-mob-dialog xo-sf-dialog${wide ? ' is-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="xo-sf-dialog-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Icon aria-hidden="true" /></span>
          <div>
            <h2 id="xo-sf-dialog-title">{title}</h2>
            {detail && <p>{detail}</p>}
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={st('close')} onClick={onClose}><X /></button>
        </header>
        {children}
      </motion.div>
    </div>
  );
}

// The field a failed save points at, from the error's body.
function failedField(error) {
  return error && error.body && typeof error.body.field === 'string' ? error.body.field : null;
}

/**
 * A new campaign, or changes to one. `channels` are the site's channel codes and
 * `currencies` the store's active currencies ({ code, name, minorUnit }).
 */
export function CampaignDialog({ site, campaign = null, channels, currencies, onClose, onSaved }) {
  const [name, setName] = useState(campaign ? campaign.name : '');
  const [channel, setChannel] = useState(campaign ? campaign.channel : (channels[0] || ''));
  const [status, setStatus] = useState(campaign ? campaign.status : 'active');
  const [notes, setNotes] = useState(campaign && campaign.notes ? campaign.notes : '');
  const [spend, setSpend] = useState(campaign && campaign.spend ? String(campaign.spend.amountMinor / (10 ** campaign.spend.minorUnit)) : '');
  const [currency, setCurrency] = useState(campaign && campaign.spend ? campaign.spend.currency : (currencies[0] ? currencies[0].code : ''));
  const [starts, setStarts] = useState(campaign ? dayValue(campaign.startsAt) : '');
  const [ends, setEnds] = useState(campaign ? dayValue(campaign.endsAt) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const first = useRef(null);
  useEffect(() => { if (first.current) first.current.focus(); }, []);

  // A saved spend in a currency the store has since switched off stays choosable.
  const choices = campaign && campaign.spend && !currencies.some((entry) => entry.code === campaign.spend.currency)
    ? [{ code: campaign.spend.currency, name: campaign.spend.currency, minorUnit: campaign.spend.minorUnit }, ...currencies] : currencies;
  const chosen = choices.find((entry) => entry.code === currency) || null;
  const spendNumber = spend.trim() === '' ? null : Number(spend.replace(',', '.'));
  const spendValid = spendNumber === null || (Number.isFinite(spendNumber) && spendNumber >= 0);
  const currencyValid = spendNumber === null || chosen !== null;
  const valid = name.trim().length > 0 && channel && spendValid && currencyValid && (!starts || !ends || dayStart(starts) <= dayStart(ends));

  async function save(event) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const details = {
      name: name.trim(), channel, status, notes: notes.trim() || null,
      spendMinor: spendNumber === null ? null : Math.round(spendNumber * (10 ** chosen.minorUnit)),
      spendCurrency: spendNumber === null ? null : chosen.code,
      startsAt: dayStart(starts), endsAt: dayEnd(ends),
    };
    try {
      const body = campaign
        ? await updateStorefrontCampaign(campaign.id, details)
        : await createStorefrontCampaign({ site: site.code, ...details });
      onSaved(body.campaign);
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  }

  const field = failedField(error);
  return (
    <Dialog icon={Megaphone} title={campaign ? st('campaigns.edit_title') : st('campaigns.new_title')}
      detail={campaign ? campaign.name : st('campaigns.new_detail')} onClose={onClose}>
      <form className="xo-mob-form xo-sf-form" onSubmit={save}>
        <label className={field === 'name' ? 'is-invalid' : ''}>
          {st('campaigns.field_name')}
          <input ref={first} value={name} maxLength={120} onChange={(event) => setName(event.target.value)} placeholder={st('campaigns.field_name_hint')} required />
        </label>
        <div className="xo-mob-form-row">
          <label className={field === 'channel' ? 'is-invalid' : ''}>
            {st('campaigns.field_channel')}
            <XomicsSelect value={channel} onChange={(event) => setChannel(event.target.value)} aria-label={st('campaigns.field_channel')}>
              {channels.map((code) => <option key={code} value={code}>{channelLabel(code)}</option>)}
            </XomicsSelect>
          </label>
          <label>
            {st('campaigns.field_status')}
            <XomicsSelect value={status} onChange={(event) => setStatus(event.target.value)} aria-label={st('campaigns.field_status')}>
              {STATUSES.map((code) => <option key={code} value={code}>{st(`campaigns.status_${code}`)}</option>)}
            </XomicsSelect>
          </label>
        </div>
        <div className="xo-mob-form-row">
          <label className={field === 'startsAt' ? 'is-invalid' : ''}>
            {st('campaigns.field_starts')}
            <input type="date" value={starts} onChange={(event) => setStarts(event.target.value)} />
          </label>
          <label className={field === 'endsAt' ? 'is-invalid' : ''}>
            {st('campaigns.field_ends')}
            <input type="date" value={ends} min={starts || undefined} onChange={(event) => setEnds(event.target.value)} />
          </label>
        </div>
        <div className="xo-mob-form-row">
          <label className={field === 'spendMinor' || !spendValid ? 'is-invalid' : ''}>
            {st('campaigns.field_spend')}
            <input inputMode="decimal" value={spend} onChange={(event) => setSpend(event.target.value)} placeholder={st('campaigns.field_spend_hint')} />
          </label>
          <label className={field === 'spendCurrency' || !currencyValid ? 'is-invalid' : ''}>
            {st('campaigns.field_currency')}
            <XomicsSelect value={currency} onChange={(event) => setCurrency(event.target.value)} aria-label={st('campaigns.field_currency')} disabled={choices.length === 0}>
              {choices.map((entry) => <option key={entry.code} value={entry.code}>{`${entry.code} · ${entry.name}`}</option>)}
            </XomicsSelect>
          </label>
        </div>
        <label className={field === 'notes' ? 'is-invalid' : ''}>
          {st('campaigns.field_notes')}
          <textarea value={notes} rows={3} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder={st('campaigns.field_notes_hint')} />
        </label>
        {error && <p className="xo-alert t-bad" role="alert">{errorMessage(error)}</p>}
        <div className="xo-mob-dialog-actions">
          <button type="button" className="xo-btn-2" onClick={onClose}>{st('cancel')}</button>
          <button type="submit" className="xo-btn" disabled={!valid || busy}>{busy ? st('saving') : campaign ? st('save') : st('campaigns.create')}</button>
        </div>
      </form>
    </Dialog>
  );
}

// Copies text and says so for a moment.
function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const timer = window.setTimeout(() => setDone(false), 1600);
    return () => window.clearTimeout(timer);
  }, [done]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
    } catch {
      setDone(false);
    }
  }
  return (
    <button type="button" className="xo-sf-copy" onClick={copy} aria-label={label} title={label}>
      {done ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}<span>{done ? st('copied') : st('copy')}</span>
    </button>
  );
}

export function UrlLine({ label, url }) {
  return (
    <div className="xo-sf-url">
      <span className="xo-sf-url-label">{label}</span>
      <code title={url}>{url}</code>
      <CopyButton text={url} label={st('copy_link', { label })} />
    </div>
  );
}

function download(name, blob) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function downloadPng(name, modules) {
  const border = 4;
  const scale = 16;
  const size = (modules.length + border * 2) * scale;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, size, size);
  context.fillStyle = '#000000';
  modules.forEach((row, y) => row.forEach((dark, x) => {
    if (dark) context.fillRect((x + border) * scale, (y + border) * scale, scale, scale);
  }));
  canvas.toBlob((blob) => { if (blob) download(name, blob); }, 'image/png');
}

/** A link's QR code, drawn crisp at any size. */
export function QrImage({ url, size = 180, label }) {
  const modules = useMemo(() => {
    try { return qrMatrix(url); } catch { return null; }
  }, [url]);
  if (!modules) return <span className="xo-sf-qr is-empty">{st('links.qr_too_long')}</span>;
  const box = modules.length + 8;
  return (
    <svg className="xo-sf-qr" width={size} height={size} viewBox={`0 0 ${box} ${box}`} shapeRendering="crispEdges" role="img" aria-label={label}>
      <rect width={box} height={box} fill="#fff" />
      <path d={qrPath(modules)} fill="#0a1e57" />
    </svg>
  );
}

// The saving buttons under a QR code: as a vector for print, as a picture for posts.
function QrDownloads({ link }) {
  const modules = useMemo(() => {
    try { return qrMatrix(link.url); } catch { return null; }
  }, [link.url]);
  if (!modules) return null;
  const name = `campaign-link-${link.code}`;
  return (
    <div className="xo-sf-qr-actions">
      <button type="button" className="xo-btn-2" onClick={() => download(`${name}.svg`, new Blob([qrSvg(modules)], { type: 'image/svg+xml' }))}>
        <Download aria-hidden="true" />{st('links.download_svg')}
      </button>
      <button type="button" className="xo-btn-2" onClick={() => downloadPng(`${name}.png`, modules)}>
        <Download aria-hidden="true" />{st('links.download_png')}
      </button>
    </div>
  );
}

export function QrDialog({ link, campaign, onClose }) {
  return (
    <Dialog icon={QrCode} title={link.label || link.code} detail={st('links.qr_detail', { campaign: campaign.name })} onClose={onClose}>
      <div className="xo-sf-qr-sheet">
        <QrImage url={link.url} size={240} label={st('links.qr_label', { url: link.url })} />
        <div className="xo-sf-qr-urls">
          <UrlLine label={st('links.short_url')} url={link.url} />
          <UrlLine label={st('links.direct_url')} url={link.directUrl} />
          <p className="xo-mob-footnote">{st('links.qr_note')}</p>
        </div>
        <QrDownloads link={link} />
      </div>
    </Dialog>
  );
}

const UTM_FIELDS = [
  ['utmSource', 'source'], ['utmMedium', 'medium'], ['utmCampaign', 'campaign'], ['utmTerm', 'term'], ['utmContent', 'content'],
];

/** A new link for a campaign, or changes to one. The code of a saved link never changes. */
export function LinkDialog({ site, campaign, link = null, onClose, onSaved }) {
  const [label, setLabel] = useState(link && link.label ? link.label : '');
  const [destination, setDestination] = useState(link ? link.destinationPath : '/');
  const [code, setCode] = useState(link ? link.code : '');
  const [utm, setUtm] = useState(() => Object.fromEntries(UTM_FIELDS.map(([key, name]) => [key, link && link.utm[name] ? link.utm[name] : ''])));
  const [showUtm, setShowUtm] = useState(Boolean(link && Object.values(link.utm).some(Boolean)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const first = useRef(null);
  useEffect(() => { if (first.current) first.current.focus(); }, []);

  const codeValid = link !== null || code === '' || CODE_PATTERN.test(code);
  const destinationValid = destination.startsWith('/') && destination.length <= 512 && !/\s/.test(destination);
  const valid = codeValid && destinationValid;
  const origin = site.publicOrigin.replace(/\/$/, '');
  const preview = `${origin}/go/${link ? link.code : code || st('links.code_placeholder')}`;

  async function save(event) {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const details = { label: label.trim() || null, destinationPath: destination.trim() };
    for (const [key] of UTM_FIELDS) details[key] = utm[key].trim() || null;
    try {
      const body = link
        ? await updateStorefrontLink(link.id, details)
        : await createStorefrontLink(campaign.id, { ...details, ...(code ? { code } : {}) });
      setSaved(body.link);
      onSaved(body.link);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (saved && !link) {
    return (
      <Dialog icon={Link2} title={st('links.ready_title')} detail={st('links.ready_detail', { campaign: campaign.name })} onClose={onClose}>
        <div className="xo-sf-qr-sheet">
          <QrImage url={saved.url} size={220} label={st('links.qr_label', { url: saved.url })} />
          <div className="xo-sf-qr-urls">
            <UrlLine label={st('links.short_url')} url={saved.url} />
            <UrlLine label={st('links.direct_url')} url={saved.directUrl} />
          </div>
          <QrDownloads link={saved} />
          <div className="xo-mob-dialog-actions"><button type="button" className="xo-btn" onClick={onClose}>{st('done')}</button></div>
        </div>
      </Dialog>
    );
  }

  const field = failedField(error);
  return (
    <Dialog icon={Link2} title={link ? st('links.edit_title') : st('links.new_title')} detail={campaign.name} onClose={onClose} wide>
      <form className="xo-mob-form xo-sf-form" onSubmit={save}>
        <label className={field === 'label' ? 'is-invalid' : ''}>
          {st('links.field_label')}
          <input ref={first} value={label} maxLength={120} onChange={(event) => setLabel(event.target.value)} placeholder={st('links.field_label_hint')} />
        </label>
        <div className="xo-mob-form-row">
          <label className={field === 'destinationPath' || !destinationValid ? 'is-invalid' : ''}>
            {st('links.field_destination')}
            <input value={destination} maxLength={512} onChange={(event) => setDestination(event.target.value)} placeholder="/shop" spellCheck={false} />
          </label>
          <label className={field === 'code' || !codeValid ? 'is-invalid' : ''}>
            {st('links.field_code')}
            <input value={code} maxLength={32} disabled={link !== null} spellCheck={false}
              onChange={(event) => setCode(event.target.value.toLowerCase())} placeholder={st('links.field_code_hint')} />
          </label>
        </div>
        <p className="xo-sf-form-note">{link ? st('links.code_fixed') : st('links.code_note')}</p>
        <button type="button" className="xo-sf-disclosure" aria-expanded={showUtm} onClick={() => setShowUtm(!showUtm)}>
          {showUtm ? st('links.utm_hide') : st('links.utm_show')}
        </button>
        {showUtm && (
          <div className="xo-sf-utm-grid">
            {UTM_FIELDS.map(([key, name]) => (
              <label key={key} className={field === key ? 'is-invalid' : ''}>
                <span>utm_{name}</span>
                <input value={utm[key]} maxLength={255} spellCheck={false}
                  placeholder={name === 'campaign' ? slug(campaign.name) : ''}
                  onChange={(event) => setUtm({ ...utm, [key]: event.target.value })} />
              </label>
            ))}
          </div>
        )}
        <div className="xo-sf-link-preview">
          <QrImage url={preview} size={96} label={st('links.qr_label', { url: preview })} />
          <div>
            <span className="xo-sf-url-label">{st('links.preview')}</span>
            <code>{preview}</code>
            <small>{st('links.lands_on', { url: `${origin}${destination}` })}</small>
          </div>
        </div>
        {error && <p className="xo-alert t-bad" role="alert">{errorMessage(error)}</p>}
        <div className="xo-mob-dialog-actions">
          <button type="button" className="xo-btn-2" onClick={onClose}>{st('cancel')}</button>
          <button type="submit" className="xo-btn" disabled={!valid || busy}>{busy ? st('saving') : link ? st('save') : st('links.create')}</button>
        </div>
      </form>
    </Dialog>
  );
}
